import React from 'react';
import { Badge, Button, Dropdown, Popover, Spin, Tooltip } from 'antd';
import { ExperimentOutlined, RadarChartOutlined, SafetyCertificateOutlined, SunOutlined, MailOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useNotifications from '../../hooks/useNotifications';
import './NotificationBell.css';

const TYPE_ICONS = {
    MENTION: 'fas fa-at',
    TEAM_BID_ASSIGNED: 'fas fa-user-check',
    PROFILE_MATCHES: 'fas fa-bullseye',
    DECISION_MATCHES: 'fas fa-landmark',
    INVITE_ACCEPTED: 'fas fa-user-plus',
};

// Types whose feature already has an antd icon elsewhere use that same icon here, so the
// notification is recognisable as the same thing the user opened: SafetyCertificateOutlined is
// the RFP risk analysis icon in both the AI menu entry and the report modal title.
const TYPE_ICON_COMPONENTS = {
    RFP_RISK_ANALYSIS_READY: SafetyCertificateOutlined,
    // Same icon as the "AI-analyysit" dropdown button these reports are generated from.
    NOTICE_INTELLIGENCE_REPORT_READY: ExperimentOutlined,
    // Same radar icon as the "Vastaavat tulevat hankkeet" menu entry and modal title.
    SIMILAR_UPCOMING_OPPORTUNITIES_READY: RadarChartOutlined,
    // Same sun icon as the Aamukatsaus sidebar entry.
    DAILY_BRIEF_READY: SunOutlined,
    WEEKLY_NEWSLETTER_READY: MailOutlined,
};

const NotificationTypeIcon = ({ type }) => {
    const IconComponent = TYPE_ICON_COMPONENTS[type];
    if (IconComponent) {
        return <IconComponent />;
    }
    return <i className={TYPE_ICONS[type] || 'fas fa-bell'} />;
};

const formatTime = (value, language) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const diffSeconds = Math.round((date.getTime() - Date.now()) / 1000);
    const abs = Math.abs(diffSeconds);
    const rtf = new Intl.RelativeTimeFormat(language || 'en', { numeric: 'auto' });
    if (abs < 60) return rtf.format(diffSeconds, 'second');
    if (abs < 3600) return rtf.format(Math.round(diffSeconds / 60), 'minute');
    if (abs < 86400) return rtf.format(Math.round(diffSeconds / 3600), 'hour');
    return rtf.format(Math.round(diffSeconds / 86400), 'day');
};

const notificationParams = (item) => {
    const params = item?.params;
    if (!params) return {};
    if (typeof params === 'object' && !Array.isArray(params)) return params;
    if (typeof params !== 'string') return {};
    try {
        const parsed = JSON.parse(params);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
        return {};
    }
};

const normalizeLinkPath = (linkPath) => {
    if (typeof linkPath !== 'string') return null;
    const trimmed = linkPath.trim();
    if (!trimmed) return null;
    if (/^https?:\/\//i.test(trimmed)) {
        try {
            const url = new URL(trimmed);
            return `${url.pathname}${url.search}${url.hash}`;
        } catch {
            return null;
        }
    }
    if (trimmed.startsWith('/')) return trimmed;
    return `/${trimmed}`;
};

const notificationLinkPath = (item) => {
    const params = notificationParams(item);
    const explicit = normalizeLinkPath(params.linkPath);
    if (explicit) return explicit;

    if (item?.type === 'TEAM_BID_ASSIGNED') {
        const query = new URLSearchParams();
        if (params.noticeId) query.set('noticeId', params.noticeId);
        query.set('openCard', '1');
        if (params.teamId) query.set('team', params.teamId);
        if (params.trackingId) query.set('trackingId', params.trackingId);
        return `/mybids?${query.toString()}`;
    }

    return null;
};
const notificationText = (t, item) => {
    const params = notificationParams(item);
    return t(`notifications.types.${item.type}`, {
        ...params,
        defaultValue: params.noticeTitle || params.agentName || params.organizationName || t('notifications.fallback', 'Notification'),
    });
};

const mentionCommentText = (item) => {
    if (item?.type !== 'MENTION') return '';
    const params = notificationParams(item);
    return params.commentText || params.commentExcerpt || '';
};

const NotificationBell = ({ iconStyle }) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const notifications = useNotifications();

    if (!notifications) return null;

    const {
        unreadCount,
        items,
        loading,
        hasMore,
        panelOpen,
        openPanel,
        closePanel,
        markRead,
        markAllRead,
        archiveItem,
        archiveAll,
        loadMore,
    } = notifications;

    const handleOpenChange = (open) => {
        if (open) {
            openPanel().catch(err => console.warn('Failed to open notifications', err));
        } else {
            closePanel();
        }
    };

    const handleItemClick = async (item) => {
        const linkPath = notificationLinkPath(item);
        closePanel();
        if (linkPath) navigate(linkPath);
        markRead(item.id).catch(err => console.warn('Failed to mark notification read', err));
    };

    const renderItem = (item) => {
        const comment = mentionCommentText(item);
        const unread = !item.readAt;
        return (
            <div
                key={item.id}
                role="button"
                tabIndex={0}
                className={`notification-item${unread ? ' notification-item--unread' : ''}`}
                onClick={() => handleItemClick(item).catch(err => console.warn('Failed to open notification', err))}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleItemClick(item).catch(err => console.warn('Failed to open notification', err));
                    }
                }}
            >
                <span className="notification-item__chip" aria-hidden="true">
                    <NotificationTypeIcon type={item.type} />
                </span>
                <span className="notification-item__body">
                    <span className="notification-item__text">{notificationText(t, item)}</span>
                    {comment && <span className="notification-item__comment">{comment}</span>}
                    <span className="notification-item__time">{formatTime(item.createdAt, i18n.language)}</span>
                </span>
                {unread && <span className="notification-item__dot" aria-hidden="true" />}
                <button
                    type="button"
                    className="notification-item__clear"
                    aria-label={t('notifications.clear', 'Tyhjennä')}
                    title={t('notifications.clear', 'Tyhjennä')}
                    onClick={(event) => {
                        event.stopPropagation();
                        archiveItem(item.id).catch(err => console.warn('Failed to clear notification', err));
                    }}
                >
                    ×
                </button>
            </div>
        );
    };

    const unreadItems = items.filter(item => !item.readAt);
    const readItems = items.filter(item => item.readAt);
    const showGroupLabels = unreadItems.length > 0;

    const moreMenu = {
        items: [
            { key: 'clearAll', danger: true, label: t('notifications.clearAll', 'Tyhjennä kaikki') },
        ],
        onClick: ({ key }) => {
            if (key === 'clearAll') {
                archiveAll().catch(err => console.warn('Failed to clear all notifications', err));
            }
        },
    };

    const content = (
        <div className="notification-popover">
            <div className="notification-popover__header">
                <span className="notification-popover__title">
                    {t('notifications.title', 'Ilmoitukset')}
                    {unreadCount > 0 && <span className="notification-popover__count">{unreadCount}</span>}
                </span>
                <span className="notification-popover__header-actions">
                    {unreadCount > 0 && (
                        <button
                            type="button"
                            className="notification-popover__markread"
                            onClick={() => markAllRead().catch(err => console.warn('Failed to mark all notifications read', err))}
                        >
                            ✓ {t('notifications.markAllRead', 'Merkitse kaikki luetuiksi')}
                        </button>
                    )}
                    {items.length > 0 && (
                        <Dropdown trigger={['click']} placement="bottomRight" menu={moreMenu}>
                            <button
                                type="button"
                                className="notification-popover__more"
                                aria-label={t('notifications.moreActions', 'Lisää toimintoja')}
                            >
                                ⋯
                            </button>
                        </Dropdown>
                    )}
                </span>
            </div>

            {loading && items.length === 0 ? (
                <div className="notification-popover__loading"><Spin size="small" /></div>
            ) : items.length === 0 ? (
                <div className="notification-popover__empty">
                    <span className="notification-popover__empty-icon" aria-hidden="true">
                        <i className="fas fa-bell" />
                    </span>
                    <div className="notification-popover__empty-title">{t('notifications.empty', 'Ei ilmoituksia')}</div>
                    <div className="notification-popover__empty-hint">
                        {t('notifications.emptyHint', 'Saat ilmoituksen, kun profiilisi löytää uusia osumia tai sinut mainitaan kommentissa.')}
                    </div>
                </div>
            ) : (
                <div className="notification-popover__list">
                    {showGroupLabels && (
                        <div className="notification-popover__group-label">{t('notifications.groupNew', 'Uudet')}</div>
                    )}
                    {unreadItems.map(renderItem)}
                    {showGroupLabels && readItems.length > 0 && (
                        <div className="notification-popover__group-label">{t('notifications.groupEarlier', 'Aiemmat')}</div>
                    )}
                    {readItems.map(renderItem)}
                </div>
            )}

            {hasMore && (
                <Button block type="text" loading={loading} onClick={() => loadMore().catch(err => console.warn('Failed to load notifications', err))}>
                    {t('notifications.loadMore', 'Lataa lisää')}
                </Button>
            )}
        </div>
    );

    return (
        <Popover trigger="click" placement="bottomRight" open={panelOpen} onOpenChange={handleOpenChange} content={content} overlayClassName="notification-popover-overlay">
            <span className="notification-bell">
                <Tooltip title={t('topNavIcons.notifications')}>
                    <Badge count={unreadCount} size="small" overflowCount={99} offset={[0, 2]}>
                        <i className="fas fa-bell" style={iconStyle} />
                    </Badge>
                </Tooltip>
            </span>
        </Popover>
    );
};

export default NotificationBell;
